// TexasRoom.jsx
import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import GamesNavbar from "../components/GamesNavbar";
import GameRoom from "../../components/GameRoom";
import SceneEffects from "../../components/scene/SceneEffects";
import { useTranslation } from "react-i18next";
import { takeSeat } from "../../services/SeatService";
import { startGame, dealFlop, dealTurn, dealRiver, endRound, approveExercise } from "../../services/TexasService";
import { useRoomRealtime } from "../../hooks/UseRoomRealtime";
import { getStableAnimalEmoji } from "../../utils/utils";
import "./TexasRoom.css";

const FIRST_TIME_INSTRUCTIONS_KEY = "animalSplit:texas:firstTimeInstructions:v1";

export default function TexasRoom() {
  const { room_id } = useParams();
  const history = useHistory();
  const { t } = useTranslation();
  const { room, loading, error, reload, seatPresence } = useRoomRealtime(room_id);
  const [playerName, setPlayerName] = useState("");
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [showRoundSummary, setShowRoundSummary] = useState(false);
  const [roundSummary, setRoundSummary] = useState(null);
  const [lastSummaryKey, setLastSummaryKey] = useState(null);
  const [copyLinkSuccess, setCopyLinkSuccess] = useState(false);
  const [showFirstTimeInstructions, setShowFirstTimeInstructions] = useState(false);

  useEffect(() => {
    const savedName = localStorage.getItem('playerName');
    if (savedName) {
      setPlayerName(savedName);
    }
  }, []);

  useEffect(() => {
    try {
      const seen = localStorage.getItem(FIRST_TIME_INSTRUCTIONS_KEY);
      if (!seen) setShowFirstTimeInstructions(true);
    } catch (e) {
      // ignore localStorage failures (private mode, etc.)
    }
  }, []);

  // Lock background scroll while the modal is open
  useEffect(() => {
    if (!showFirstTimeInstructions) return;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;

    // Prevent layout shift when scrollbar disappears
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [showFirstTimeInstructions]);

  useEffect(() => {
    if (room?.data?.roundSummary && room.data.roundSummary.length > 0) {
      const summaryKey = JSON.stringify(room.data.roundSummary);
      if (summaryKey !== lastSummaryKey) {
        setRoundSummary(room.data.roundSummary);
        setShowRoundSummary(true);
        setLastSummaryKey(summaryKey);
      }
    }
  }, [room?.data?.roundSummary, lastSummaryKey]);

  const dismissFirstTimeInstructions = () => {
    try {
      localStorage.setItem(FIRST_TIME_INSTRUCTIONS_KEY, "1");
    } catch (e) {
      // ignore localStorage failures (private mode, etc.)
    }
    setShowFirstTimeInstructions(false);
  };


  const handleLeaveRoom = () => {
    history.push(`${process.env.PUBLIC_URL}/games/categories`);
  };

  const handleSeatClick = (seatPosition) => {
    if (!room || !room.data || !room.data.seats) return;

    const seat = room.data.seats[seatPosition];
    if (!seat.playerName && room.status === 'playing') {
      alert('Game already started. Please wait for the next game.');
      return;
    }
    
    // If seat is already taken, navigate to that seat page
    if (seat.playerName) {
      history.push(`${process.env.PUBLIC_URL}/games/texas/${room_id}/seat/${seatPosition}`);
      return;
    }

    // Seat is available - always prompt for player name
    setSelectedSeat(seatPosition);
    setShowNamePrompt(true);
  };

  const handleTakeSeat = async (seatPosition) => {
    if (room?.status === 'playing') {
      alert('Game already started. Please wait for the next game.');
      setShowNamePrompt(false);
      setSelectedSeat(null);
      return;
    }
    try {
      await takeSeat(room_id, seatPosition, playerName);
      setShowNamePrompt(false);
      setSelectedSeat(null);
      
      // Redirect to player's seat view with seat index
      history.push(`${process.env.PUBLIC_URL}/games/texas/${room_id}/seat/${seatPosition}`);
    } catch (error) {
      console.error("Failed to take seat:", error);
      alert(error.response?.data?.message || "Failed to take seat");
    }
  };

  const handleStartGame = async () => {
    try {
      await startGame(room_id);
    } catch (error) {
      console.error("Failed to start game:", error);
      alert(error.response?.data?.message || "Failed to start game");
    }
  };

  const handleDealFlop = async () => {
    if (!bettingComplete) {
      alert("⏳ Please wait! Not all players have matched their bets yet.");
      return;
    }
    try {
      await dealFlop(room_id);
    } catch (error) {
      console.error("Failed to deal flop:", error);
      alert(error.response?.data?.message || "Failed to deal flop");
    }
  };

  const handleDealTurn = async () => {
    if (!bettingComplete) {
      alert("⏳ Please wait! Not all players have matched their bets yet.");
      return;
    }
    try {
      await dealTurn(room_id);
    } catch (error) {
      console.error("Failed to deal turn:", error);
      alert(error.response?.data?.message || "Failed to deal turn");
    }
  };

  const handleDealRiver = async () => {
    if (!bettingComplete) {
      alert("⏳ Please wait! Not all players have matched their bets yet.");
      return;
    }
    try {
      await dealRiver(room_id);
    } catch (error) {
      console.error("Failed to deal river:", error);
      alert(error.response?.data?.message || "Failed to deal river");
    }
  };

  const handleEndRound = async () => {
    try {
      const updatedRoom = await endRound(room_id);
      if (updatedRoom && updatedRoom.data && updatedRoom.data.roundSummary) {
        setRoundSummary(updatedRoom.data.roundSummary);
        setShowRoundSummary(true);
        setLastSummaryKey(JSON.stringify(updatedRoom.data.roundSummary));
      }
    } catch (error) {
      console.error("Failed to end round:", error);
      alert(error.response?.data?.message || "Failed to end round");
    }
  };

  const handleAddMoney = async (seatPosition) => {
    // Placeholder: not persisted; prompt user instead of mutating local state
    alert("Add chips not implemented yet");
  };

  const handleApproveExercise = async (exerciseId, approve) => {
    try {
      await approveExercise(room_id, exerciseId, approve);
      // Room will update automatically via websocket (room:update)
    } catch (error) {
      console.error("Failed to approve exercise:", error);
      alert("Failed to approve exercise");
    }
  };

  const currentRound = room && room.data && room.data.stage;

  // Check if betting round is complete (all active players have same bet or are all-in)
  const isBettingComplete = () => {
    if (!room || !room.data || !room.data.seats) return false;
    
    const activePlayers = room.data.seats.filter(seat => 
      seat.playerName && seat.status !== 'folded' && seat.status !== 'empty'
    );

    if (activePlayers.length === 0) return false;
    if (activePlayers.length === 1) return true; // Only one player left

    // Get highest bet amount
    const maxBet = Math.max(...activePlayers.map(s => s.bet || 0));
    
    // Special case for preflop: need at least some betting (blinds should create stagePot > 0)
    const stagePot = room.data.stagePot || 0;
    if (currentRound === 'preflop' && stagePot === 0 && maxBet === 0) {
      return false; // Preflop must have blinds
    }
    
    // For post-flop rounds: if maxBet is 0 and stagePot is 0, all players checked - that's valid!
    // Just need to verify all players have matching bets (all 0)
    
    // Check if all players either matched the max bet or are all-in
    const allMatched = activePlayers.every(seat => {
      const hasMatchedBet = (seat.bet || 0) === maxBet;
      const isAllIn = seat.chips === 0;
      return hasMatchedBet || isAllIn;
    });

    return allMatched;
  };

  const bettingComplete = isBettingComplete();

  const copyRoomLink = () => {
    const roomUrl = `${window.location.origin}${process.env.PUBLIC_URL}/games/texas/${room_id}`;
    navigator.clipboard.writeText(roomUrl).then(() => {
      setCopyLinkSuccess(true);
      setTimeout(() => setCopyLinkSuccess(false), 2000);
    }).catch(err => {
      console.error('Failed to copy link:', err);
      alert('Failed to copy link');
    });
  };

  const isRedSuit = (card) => {
    return card && (card.includes('♥') || card.includes('♦'));
  };

  const getResultLabel = (chipChange) => {
    if (chipChange > 0) return 'WIN';
    if (chipChange < 0) return 'LOSE';
    return 'PUSH';
  };

  const formatChipChange = (chipChange) => {
    if (chipChange > 0) return `+$${chipChange}`;
    if (chipChange < 0) return `-$${Math.abs(chipChange)}`;
    return '$0';
  };

  const getHandRankDisplay = (summary) => {
    if (!summary) return '--';
    if (summary.finalStatus === 'folded') {
      return summary.handRank ? `${summary.handRank} (folded)` : 'Folded';
    }
    return summary.handRank || '--';
  };

  const getBoardCards = () => {
    if (!roundSummary || roundSummary.length === 0) return [];
    return roundSummary[0].communityCards || [];
  };

  const shouldShowHoleCards = (summary) => {
    if (!summary) return false;
    if (summary.finalStatus === 'folded') return false;

    const winners = roundSummary?.filter(s => s.isWinner) || [];
    const everyoneElseFolded =
      winners.length === 1 &&
      summary.isWinner &&
      (roundSummary || []).every(s => s === summary || s.finalStatus === 'folded');

    if (everyoneElseFolded) return false;

    return summary.holeCards && summary.holeCards.length > 0;
  };

  const handleNameSubmit = () => {
    if (!playerName.trim()) {
      alert("Please enter your name");
      return;
    }

    localStorage.setItem('playerName', playerName);
    
    if (selectedSeat !== null) {
      handleTakeSeat(selectedSeat);
    }
  };

  return (
    <GameRoom room={room} loading={loading} error={error} onRetry={reload}>
      {(roomData) => (
    <div className="texas-room as-theme">
      <GamesNavbar />
      <SceneEffects fluffCount={26} />
      
      <div className="texas-room-content">
        {showFirstTimeInstructions && (
          <div
            className="first-time-instructions-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={t("games.texas.instructions.title")}
          >
            {/* NOTE: This is a custom overlay (not a Bootstrap modal). Avoid `.as-modal`
               because shared modal tweaks (e.g. overflow for dropdowns) can break the
               scroll/layout of this instruction box. */}
            <div className="first-time-instructions-box as-theme">
              <button
                type="button"
                className="first-time-instructions-close"
                onClick={dismissFirstTimeInstructions}
                aria-label={t("general.close")}
                title={t("general.close")}
              >
                ×
              </button>

              <div className="first-time-instructions-header">
                <div className="first-time-instructions-stickers" aria-hidden="true">
                  <span className="fti-sticker fti-sticker--1">🐶</span>
                  <span className="fti-sticker fti-sticker--2">🐱</span>
                  <span className="fti-sticker fti-sticker--3">🐰</span>
                  <span className="fti-sticker fti-sticker--4">🦊</span>
                  <span className="fti-sticker fti-sticker--5">🐻</span>
                </div>
                <h2 className="first-time-instructions-title">
                  {t("games.texas.instructions.title")}
                </h2>
                <div className="first-time-instructions-subtitle">
                  {t("games.texas.instructions.subtitle")}
                </div>
              </div>

              <div className="first-time-instructions-mock-grid">
                <div className="fti-mock-card fti-mock-card--admin">
                  <div className="fti-mock-tag">👑 {t("games.texas.instructions.admin_tag")}</div>
                  <div className="fti-mock-title">{t("games.texas.instructions.admin_title")}</div>
                  <div className="fti-mock-subtitle">{t("games.texas.instructions.admin_desc")}</div>

                  <div className="fti-mock-panel">
                    <div className="fti-mock-panel-title">🎛 {t("games.texas.instructions.mock_controls_title")}</div>
                    <div className="fti-mock-buttons" aria-hidden="true">
                      <div className="fti-mock-button">🎮 {t("games.texas.instructions.mock_button_start")}</div>
                      <div className="fti-mock-button">🃏 {t("games.texas.instructions.mock_button_deal")}</div>
                      <div className="fti-mock-button">✅ {t("games.texas.instructions.mock_button_approve")}</div>
                    </div>
                    <div className="fti-mock-hint">{t("games.texas.instructions.admin_hint")}</div>
                  </div>
                </div>

                <div className="fti-mock-card fti-mock-card--player">
                  <div className="fti-mock-tag">🪑 {t("games.texas.instructions.player_tag")}</div>
                  <div className="fti-mock-title">{t("games.texas.instructions.player_title")}</div>
                  <div className="fti-mock-subtitle">{t("games.texas.instructions.player_desc")}</div>

                  <div className="fti-mock-panel">
                    <div className="fti-mock-panel-title">🃏 {t("games.texas.instructions.mock_seat_title")}</div>
                    <div className="fti-seat-preview fti-seat-preview--available" aria-hidden="true">
                      <div className="fti-seat-preview-header">
                        <span className="fti-seat-preview-seat">
                          {t("games.texas.instructions.seat_label")} 1
                        </span>
                        <span className="fti-seat-preview-status fti-seat-preview-status--available">
                          <span className="fti-seat-dot fti-seat-dot--available" /> {t("games.texas.instructions.seat_status_available")}
                        </span>
                      </div>
                      <div className="fti-seat-preview-main">
                        <div className="fti-seat-preview-name">🪑 {t("games.texas.instructions.seat_empty")}</div>
                        <div className="fti-seat-preview-chips">💰 —</div>
                      </div>
                      <div className="fti-seat-preview-cta">{t("games.texas.instructions.seat_cta")} →</div>
                    </div>
                    <div className="fti-mock-hint">{t("games.texas.instructions.player_hint")}</div>
                  </div>
                </div>
              </div>

              <div className="first-time-instructions-tips">
                <div className="first-time-instructions-tips-title">
                  {t("games.texas.instructions.tips_title")}
                </div>
                <ul className="first-time-instructions-list">
                  <li>{t("games.texas.instructions.line1")}</li>
                  <li>{t("games.texas.instructions.line2")}</li>
                  <li>{t("games.texas.instructions.line3")}</li>
                </ul>
              </div>

              <div className="first-time-instructions-actions">
                <button
                  type="button"
                  className="btn as-btn-primary-gradient"
                  onClick={dismissFirstTimeInstructions}
                >
                  {t("games.texas.instructions.got_it")}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="room-header as-panel">
          <div className="as-panel-header">
            <div className="as-panel-title truncate">
              <span className="as-icon-badge" aria-hidden="true">♠️</span>
              <span className="truncate">{room.name}</span>
            </div>
            <div className="as-panel-actions">
              <button
                type="button"
                className="btn btn-sm as-btn-soft"
                onClick={copyRoomLink}
                title="Copy room link to share"
              >
                {copyLinkSuccess ? "✓ Link Copied!" : `${getStableAnimalEmoji(`texas:${room_id}:share-link`)} 🔗 Share`}
              </button>
            </div>
          </div>
          <div className="as-panel-body">
            <div className="texas-animal-row">🐶🐱🐰🦊🐻🐼🐨🐯</div>
            <div className="room-info">
              <span>Blinds: ${room.data.smallBlind}/${room.data.bigBlind}</span>
              <span className={`room-status ${room.status}`}>
                {room.status}
              </span>
            </div>
          </div>
        </div>

        {/* Game Controls - Available to anyone with room link */}
        <div className="admin-controls as-panel">
          <div className="as-panel-header">
            <div className="as-panel-title">
              <span className="as-icon-badge" aria-hidden="true">🎛</span>
              <span>Game Controls</span>
              {currentRound && (
                <span className={`betting-status ${bettingComplete ? 'complete' : 'incomplete'}`}>
                  {bettingComplete ? ' ✅ Ready to proceed' : ' ⏳ Waiting for bets'}
                </span>
              )}
            </div>
          </div>
          <div className="as-panel-body">
            <div className="admin-controls-buttons">
              {(!currentRound || currentRound === 'showdown') && (
                <button className="btn btn-lg as-btn-primary-gradient" onClick={handleStartGame}>
                  {getStableAnimalEmoji(`texas:${room_id}:start-game`)} 🎮 Start Game
                </button>
              )}
              {currentRound === 'preflop' && (
                <button 
                  className="btn btn-lg as-btn-info-gradient" 
                  onClick={handleDealFlop}
                >
                  {getStableAnimalEmoji(`texas:${room_id}:deal-flop`)} 🃏 Deal Flop (3 Cards)
                </button>
              )}
              {currentRound === 'flop' && (
                <button 
                  className="btn btn-lg as-btn-info-gradient" 
                  onClick={handleDealTurn}
                >
                  {getStableAnimalEmoji(`texas:${room_id}:deal-turn`)} 🃏 Deal Turn (4th Card)
                </button>
              )}
              {currentRound === 'turn' && (
                <button 
                  className="btn btn-lg as-btn-info-gradient" 
                  onClick={handleDealRiver}
                >
                  {getStableAnimalEmoji(`texas:${room_id}:deal-river`)} 🃏 Deal River (5th Card)
                </button>
              )}
              {currentRound && currentRound !== 'showdown' && (
                <button className="btn btn-lg as-btn-warning-gradient" onClick={handleEndRound}>
                  {getStableAnimalEmoji(`texas:${room_id}:end-game`)} 🏁 End Game
                </button>
              )}
            </div>
          </div>
        </div>

        {showNamePrompt && (
          <div className="name-prompt-overlay">
            <div className="name-prompt-box as-modal as-theme">
              <h3>Enter Your Name</h3>
              <input
                type="text"
                className="form-control"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Your name..."
                maxLength={20}
                onKeyPress={(e) => e.key === 'Enter' && handleNameSubmit()}
              />
              <div className="name-prompt-buttons">
                <button type="button" className="btn as-btn-primary-gradient" onClick={handleNameSubmit}>
                  Confirm
                </button>
                <button 
                  type="button"
                  className="btn as-btn-soft" 
                  onClick={() => {
                    setShowNamePrompt(false);
                    setSelectedSeat(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showRoundSummary && roundSummary && (
          <div className="round-summary-overlay">
            <div className="round-summary-box as-modal as-theme">
              <h2>🏁 Round Summary</h2>
              {getBoardCards().length > 0 && (
                <div className="summary-board-top">
                  <span className="summary-hand-label">Board</span>
                  <div className="summary-card-row">
                    {getBoardCards().map((card, idx) => (
                      <div key={idx} className={`card ${isRedSuit(card) ? 'red-card' : ''}`}>
                        {card}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="round-summary-list">
                {roundSummary.map((summary, index) => {
                  const resultLabel = getResultLabel(summary.chipChange);
                  const handRankDisplay = getHandRankDisplay(summary);
                  const showHole = shouldShowHoleCards(summary);

                  return (
                    <div
                      key={index}
                      className={`summary-item ${
                        summary.chipChange > 0 ? 'win' : 
                        summary.chipChange < 0 ? 'loss' : 'neutral'
                      }`}
                    >
                      <div className="summary-info">
                        <div className="summary-top-row">
                          <span className="summary-seat">Seat {summary.seatPosition + 1}</span>
                          <span className={`summary-result-tag ${resultLabel.toLowerCase()}`}>
                            {resultLabel}
                          </span>
                        </div>
                        <div className="summary-name-row">
                          <span className="summary-name">
                            {summary.playerName}
                            {summary.isWinner && <span className="winner-badge"> 🏆 WINNER</span>}
                            {summary.finalStatus === 'folded' && (
                              <span className="summary-folded-tag">Folded</span>
                            )}
                          </span>
                          <span className="summary-change">
                            {formatChipChange(summary.chipChange)}
                          </span>
                        </div>
                        <div className="summary-hand-line">
                          <span className="summary-hand-label">Hand</span>
                          <span className="summary-hand-rank">{handRankDisplay}</span>
                        </div>
                        {showHole && (
                          <div className="summary-cards">
                            <div className="summary-hand-cards">
                              <span className="summary-hand-label">Hole</span>
                              <div className="summary-card-row">
                                {summary.holeCards.map((card, idx) => (
                                  <div key={idx} className={`card ${isRedSuit(card) ? 'red-card' : ''}`}>
                                    {card}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <button 
                className="btn as-btn-primary-gradient" 
                onClick={() => {
                  setShowRoundSummary(false);
                  setRoundSummary(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Seats Section */}
        <div className="seats-section">
          <h2 className="seats-title">Players & Seats</h2>
          <div className="seats-grid">
            {room.data && room.data.seats && room.data.seats.map((seat, index) => (
              <div
                key={index}
                className={`seat-card ${seat.playerName ? 'occupied' : 'empty'} ${seat.status === 'folded' ? 'folded' : ''}`}
                onClick={() => handleSeatClick(index)}
                style={{ cursor: seat.playerName ? 'pointer' : 'pointer' }}
              >
                <div className="seat-card-header">
                  <div className="seat-number">Seat {index + 1}</div>
                  <div className={`seat-connection ${seatPresence?.includes(index) ? 'online' : 'offline'}`}>
                    <span className={`status-dot ${seatPresence?.includes(index) ? 'online' : 'offline'}`} />
                    {seatPresence?.includes(index) ? 'Connected' : 'Offline'}
                  </div>
                </div>

                {seat.playerName ? (
                  <div className="seat-card-main">
                    <div className="seat-name-row">
                      <span className="seat-player-name">
                        {getStableAnimalEmoji(`texas:${room_id}:seat:${index}:player:${seat.playerName}`)} {seat.playerName}
                      </span>
                      {seatPresence?.includes(index) ? (
                        <span className="seat-player-chips">💰 ${seat.chips}</span>
                      ) : (
                        <span className="seat-player-chips seat-player-chips--hidden">💤</span>
                      )}
                    </div>
                    {seatPresence?.includes(index) && seat.bet > 0 && (
                      <div className="seat-player-bet">Bet: ${seat.bet}</div>
                    )}
                    <div className="seat-status-row">
                      <span className={`seat-status ${seat.status === 'folded' ? 'folded' : ''}`}>
                        {seat.status || 'ready'}
                      </span>
                    </div>

                    {seat.chips <= 0 && (
                      <div className="seat-actions">
                        <button 
                          className="btn-seat-action success"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddMoney(index);
                          }}
                        >
                          💵 +$1000
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="seat-empty compact-empty">
                    <div className="empty-text">Available</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Poker Table */}
        <div className="poker-table-container">
          <div className="table-center">
            <h2>♠️♥️♣️♦️</h2>
            
            {/* Community Cards */}
            {room.data && room.data.communityCards && room.data.communityCards.length > 0 ? (
              <div className="community-cards-display">
                <div className="community-cards-label">Community Cards</div>
                <div className="community-cards">
                  {room.data.communityCards.map((card, index) => (
                    <div key={index} className={`card ${isRedSuit(card) ? 'red-card' : ''}`}>
                      {card}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-cards-message">
                {currentRound ? 'Waiting for cards...' : 'Game not started'}
              </div>
            )}
            
            <div className="table-pot">
              <p>Pot: ${room.data && room.data.pot ? room.data.pot : 0}</p>
            </div>
            
            {currentRound && (
              <div className="current-round-display">
                Round: <span className="round-name">{currentRound.toUpperCase()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Exercise Pending Requests Section */}
        {room.data && room.data.exercises && room.data.exercises.filter(ex => ex.status === 'pending').length > 0 && (
          <div className="exercise-section">
            <h2 className="section-title">💪 Pending Exercise Requests</h2>
            <div className="exercise-list">
              {room.data.exercises
                .filter(ex => ex.status === 'pending')
                .map((exercise) => (
                  <div key={exercise.id} className="exercise-item pending">
                    <div className="exercise-info">
                      <div className="exercise-player">
                        <strong>Seat {exercise.seatPosition + 1}</strong> - {exercise.playerName}
                      </div>
                      <div className="exercise-type">{exercise.exerciseType}</div>
                    </div>
                    <div className="exercise-actions">
                      <button 
                        className="btn-exercise approve"
                        onClick={() => handleApproveExercise(exercise.id, true)}
                      >
                        ✅ Approve
                      </button>
                      <button 
                        className="btn-exercise reject"
                        onClick={() => handleApproveExercise(exercise.id, false)}
                      >
                        ❌ Reject
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Exercise History Section */}
        {room.data && room.data.exercises && room.data.exercises.filter(ex => ex.status === 'approved').length > 0 && (
          <div className="exercise-section">
            <h2 className="section-title">📋 Exercise History</h2>
            <div className="exercise-history">
              {(() => {
                // Group exercises by player name
                const approvedExercises = room.data.exercises.filter(ex => ex.status === 'approved');
                const groupedByPlayer = approvedExercises.reduce((acc, exercise) => {
                  if (!acc[exercise.playerName]) {
                    acc[exercise.playerName] = [];
                  }
                  acc[exercise.playerName].push(exercise);
                  return acc;
                }, {});

                return Object.entries(groupedByPlayer).map(([playerName, exercises]) => (
                  <div key={playerName} className="player-exercise-group">
                    <h3 className="player-name-header">
                      {playerName} <span className="exercise-count">({exercises.length} exercises)</span>
                    </h3>
                    <div className="player-exercises">
                      {exercises.map((exercise) => (
                        <div key={exercise.id} className="exercise-item approved">
                          <div className="exercise-type">{exercise.exerciseType}</div>
                          <div className="exercise-meta">
                            <span className="exercise-reward">+$1000</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {/* Donation History Section */}
        {room.data && room.data.donations && room.data.donations.length > 0 && (
          <div className="exercise-section donation-section">
            <h2 className="section-title">💝 Donation Supporters</h2>
            <div className="donation-history">
              {(() => {
                // Group donations by player name
                const groupedByPlayer = room.data.donations.reduce((acc, donation) => {
                  if (!acc[donation.playerName]) {
                    acc[donation.playerName] = [];
                  }
                  acc[donation.playerName].push(donation);
                  return acc;
                }, {});

                return Object.entries(groupedByPlayer).map(([playerName, donations]) => {
                  const totalAmount = donations.reduce((sum, d) => sum + d.amount, 0);
                  const totalChips = donations.reduce((sum, d) => sum + d.chipsEarned, 0);
                  
                  return (
                    <div key={playerName} className="player-exercise-group donation-group">
                      <h3 className="player-name-header">
                        {playerName} 
                        <span className="exercise-count">
                          ({donations.length} donation{donations.length > 1 ? 's' : ''})
                        </span>
                        <span className="donation-total">
                          ${(totalAmount / 100).toFixed(2)} → {totalChips} chips
                        </span>
                      </h3>
                      <div className="player-exercises">
                        {donations.map((donation) => (
                          <div key={donation.id} className="exercise-item donation-item">
                            <div className="donation-detail">
                              <div className="donation-amount-display">
                                ${(donation.amount / 100).toFixed(2)}
                              </div>
                              <div className="exercise-type">{donation.alias}</div>
                            </div>
                            <div className="exercise-meta">
                              <span className="exercise-time">
                                {new Date(donation.donatedAt).toLocaleDateString()}
                              </span>
                              <span className="exercise-reward">+${donation.chipsEarned}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        <div className="room-controls">
          <button 
            className="btn as-btn-soft"
            onClick={handleLeaveRoom}
          >
            Leave Room
          </button>
        </div>
      </div>
    </div>
      )}
    </GameRoom>
  );
}
